import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import {
  Currency,
  ExternalBankAccount,
  InternalAccountType
} from "@wingspanhq/wingspan-api-sdk";
import { useState } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { selectorExternalBankAccountDepositReady } from "@selectors/selectorExternalBankAccountDepositReady";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { FormManuallyAddAccount } from "../../../../shared/components/FormManuallyAddAccount";
import { FormType } from "../../../../shared/components/FormManuallyAddAccount/FormType";
import {
  ManualAccountSubType,
  ManualAccountType
} from "../../../../shared/components/FormManuallyAddAccount/types";
import { StepLayoutWithOptions } from "../../../../shared/components/Layout/StepLayoutWithOptions";
import {
  avatarBankAccount,
  avatarPlaid,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { useExternalBankAccountsPlaidLink } from "../../../../shared/hooks/useExternalBankAccountsPlaidLink";
import { FormCreateInternalAccount } from "../../../Onboarding/components/FormCreateInternalAccount";
import { FormSelectAccount } from "../FormSelectAccount";

export type PayrollFundingMethodType =
  | "ach"
  | "wallet-usd"
  | "wallet-cad"
  | "wallet-pln";
export type PayrollFundingMethodACHType = "plaid" | "manual";

type Props = {
  basePath: string;
  onBack?: () => void;
  onNext?: () => void;
  availableMethods?: PayrollFundingMethodType[];
};

// Empty/undefined availableMethods means "no restriction" — show everything.
const filterOptionsByAvailableMethods = <O extends { value: string }>(
  options: O[],
  availableMethods: PayrollFundingMethodType[] | undefined
): O[] => {
  if (!availableMethods) {
    return options;
  }
  const allowedValues = availableMethods as string[];
  return options.filter(option => allowedValues.includes(option.value));
};

export const FlowAddFundingMethod: React.FC<Props> = ({
  basePath,
  onBack,
  onNext,
  availableMethods
}) => {
  const history = useHistory();

  const queryFeatureFlags = useFeatureFlags();

  const [createdAccounts, setCreatedAccounts] = useState<ExternalBankAccount[]>(
    []
  );
  const { openSnackbar } = useWSSnackbar();
  const plaidHandler = useExternalBankAccountsPlaidLink({
    onSuccess: accounts => {
      const filteredAccounts = accounts.filter(
        selectorExternalBankAccountDepositReady
      );

      if (filteredAccounts.length > 0) {
        setCreatedAccounts(filteredAccounts);
        history.push(basePath + "/accounts");
      } else {
        openSnackbar({
          type: "error",
          message: "Account can not be used as a payroll funding method"
        });
      }
    }
  });

  const [manualAccountType, setManualAccountType] = useState<{
    type: ManualAccountType;
    subType: ManualAccountSubType;
  }>({
    type: "personal",
    subType: "checking"
  });

  return (
    <Switch>
      <Route path={basePath + "/type"}>
        <StepLayoutWithOptions<PayrollFundingMethodType>
          key="type"
          title="Add payroll funding method"
          description="Add a payroll funding method to fund your payrolls and start sending payments"
          options={filterOptionsByAvailableMethods(
            queryFeatureFlags.data?.prefundingMethods
              ? [
                  {
                    header: {
                      label: {
                        text: "Bank account (USD)",
                        helperText: "Connect your bank account securely",
                        avatar: avatarBankAccount
                      }
                    },
                    value: "ach"
                  },
                  {
                    header: {
                      label: {
                        text: "Wingspan Wallet pre-funding (USD)",
                        helperText:
                          "Pre-fund and send payments with a free business banking account",
                        avatar: avatarWallet
                      }
                    },
                    value: "wallet-usd"
                  },
                  {
                    header: {
                      label: {
                        text: "Wingspan Wallet pre-funding (CAD)",
                        helperText:
                          "Pre-fund and send payments in Canadian Dollars",
                        avatar: {
                          format: "square",
                          type: "country",
                          country: "Canada"
                        }
                      }
                    },
                    value: "wallet-cad"
                  },
                  {
                    header: {
                      label: {
                        text: "Wingspan Wallet pre-funding (PLN)",
                        helperText:
                          "Pre-fund and send payments in Polish Złoty",
                        avatar: {
                          format: "square",
                          type: "country",
                          country: "Poland"
                        }
                      }
                    },
                    value: "wallet-pln"
                  }
                ]
              : [
                  {
                    header: {
                      label: {
                        text: "Bank account (USD)",
                        helperText: "Connect your bank account securely",
                        avatar: avatarBankAccount
                      }
                    },
                    value: "ach"
                  }
                ],
            availableMethods
          )}
          onBack={onBack}
          onNext={value => {
            history.push(basePath + "/" + value);
          }}
        />
      </Route>

      <Route path={basePath + "/ach"}>
        <StepLayoutWithOptions<PayrollFundingMethodACHType>
          key="ach"
          title="Add bank account"
          description="Add a bank account as your default payroll funding method"
          options={[
            {
              header: {
                label: {
                  text: "Link account with Plaid",
                  helperText:
                    "Instantly verify and connect your bank accounts securely",
                  avatar: avatarPlaid
                }
              },
              value: "plaid"
            },
            {
              header: {
                label: {
                  text: "Set up account manually",
                  helperText:
                    "Manually enter your routing and account numbers and verify via micro-deposits",
                  avatar: avatarBankAccount
                }
              },
              value: "manual"
            }
          ]}
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onNext={value => {
            if (value === "plaid") {
              plaidHandler.open();
            } else {
              history.push(basePath + "/manual-type");
            }
          }}
        />
      </Route>

      <Route path={basePath + "/wallet-usd"}>
        <FormCreateInternalAccount
          type={InternalAccountType.BANKING}
          currency={Currency.USD}
          setFundingSource
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onContinue={onNext}
        />
      </Route>

      <Route path={basePath + "/wallet-cad"}>
        <FormCreateInternalAccount
          type={InternalAccountType.INTERNATIONAL_CLEARING}
          currency={Currency.CAD}
          setFundingSource
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onContinue={onNext}
        />
      </Route>

      <Route path={basePath + "/wallet-pln"}>
        <FormCreateInternalAccount
          type={InternalAccountType.AP_PREFUNDING}
          currency={Currency.PLN}
          setFundingSource
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onContinue={onNext}
        />
      </Route>

      <Route path={basePath + "/accounts"}>
        <FormSelectAccount
          title="Select bank account"
          description="Select a bank account as your default payroll funding method"
          setFundingSource
          accounts={createdAccounts}
          onBack={() => {
            history.push(basePath + "/ach");
          }}
          onNext={onNext}
        />
      </Route>

      <Route path={basePath + "/manual-type"}>
        <FormType
          value={manualAccountType}
          onBack={() => {
            history.push(basePath + "/ach");
          }}
          onNext={newValue => {
            setManualAccountType(newValue);
            history.push(basePath + "/manual");
          }}
        />
      </Route>

      <Route path={basePath + "/manual"}>
        <FormManuallyAddAccount
          setFundingSource
          requestVerification
          onBack={() => {
            history.push(basePath + "/manual-type");
          }}
          onSuccess={onNext}
          withPanel
          type={manualAccountType.type}
          subType={manualAccountType.subType}
        />
      </Route>

      <Redirect path={basePath} to={basePath + "/type"} />
    </Switch>
  );
};
